/*
 * Preview — VERQO brand exploration
 *
 * Design-only mockup of logos + taglines + color palettes for the proposed
 * VERQO rename. Hit /preview/verqo-brand to view.
 *
 * No production code is touched. Production logo stays TRAQO until the user
 * picks a direction.
 *
 * Page sections:
 *   1. Logo directions  — 6 distinct treatments, each rendered as inline SVG
 *      so they print crisp at any size and any color.
 *   2. Applied mockups  — favicon · login header · email signature · invoice
 *      footer · mobile app icon · seller storefront header.
 *   3. Color palettes   — 4 candidate palettes with their core swatches.
 *   4. Tagline shortlist — 10 candidates with quick rationale.
 *   5. Typography pairing recommendation.
 */
import { useState } from "react";

// Central brand tokens used to drive every preview. Swap palette here and
// every mockup updates because each component reads from `palette`.
const PALETTES = {
  oxford_lime: {
    name: "Oxford + Lime",
    sub: "Confident, slightly underground. Inherits TRAQO's energy — easy continuity.",
    bg: "#0c1612",
    surface: "#0f1f1a",
    fg: "#f4f7f1",
    muted: "rgba(244,247,241,0.6)",
    accent: "#dcff3a",
    accent_fg: "#0c1612",
  },
  emerald_cream: {
    name: "Emerald + Cream",
    sub: "Premium B2B. Reads like Stripe or Plaid — calm, trustworthy.",
    bg: "#0a3d2c",
    surface: "#0c4a35",
    fg: "#f5f1e8",
    muted: "rgba(245,241,232,0.65)",
    accent: "#f5f1e8",
    accent_fg: "#0a3d2c",
  },
  indigo_saffron: {
    name: "Indigo + Saffron",
    sub: "Editorial / fintech. Distinctive against everyone using gradient-purple.",
    bg: "#15163d",
    surface: "#1b1d4f",
    fg: "#f8f5ef",
    muted: "rgba(248,245,239,0.7)",
    accent: "#ffb840",
    accent_fg: "#15163d",
  },
  graphite_chartreuse: {
    name: "Graphite + Chartreuse",
    sub: "Industrial · architectural. Reads like an enterprise infra tool.",
    bg: "#1a1a1a",
    surface: "#222222",
    fg: "#fafafa",
    muted: "rgba(250,250,250,0.6)",
    accent: "#caff5b",
    accent_fg: "#1a1a1a",
  },
};

const TAGLINES = [
  { line: "The trust layer for B2B trade.",     why: "Direct, defensible, positions VERQO as infra not just a marketplace." },
  { line: "Wholesale, verified.",                why: "2 words. Plays on the 'ver' in VERQO. Cleanest." },
  { line: "B2B commerce, end to end.",           why: "Investor-friendly. Sounds like a category leader." },
  { line: "Where wholesale runs on trust.",      why: "Editorial. Good for landing-page hero or homepage tagline." },
  { line: "Trade on rails.",                     why: "Stripe-like — bold, infra-flavored, 3 words." },
  { line: "Run your wholesale. Simply.",         why: "Operator-friendly. Better for buyer-facing onboarding." },
  { line: "The wholesale operating system.",     why: "Inherits the TRAQO 'OS' positioning. Long but strong." },
  { line: "Verified buyers. Trusted suppliers.", why: "Two-sided value prop in one breath." },
  { line: "Pay later, prebook earlier.",         why: "Product-led — leans on credit + prebook unlocks." },
  { line: "Wholesale you can verify.",           why: "Echoes the brand name twice. Memorable." },
];

const TABS = [
  { id: "logos",    label: "Logo directions" },
  { id: "applied",  label: "Applied mockups" },
  { id: "palettes", label: "Color palettes" },
  { id: "tagline",  label: "Tagline shortlist" },
  { id: "type",     label: "Typography" },
];

export default function VerqoBrandPreview() {
  const [tab, setTab] = useState("logos");
  const [paletteKey, setPaletteKey] = useState("oxford_lime");
  const palette = PALETTES[paletteKey];
  return (
    <div className="min-h-screen" style={{ background: "#f6f6f4" }}>
      {/* Header */}
      <div className="border-b border-black/10 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-black/45">Brand exploration · preview only</div>
            <div className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "'Inter', system-ui" }}>VERQO — visual identity options</div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-black/55">Live palette:</span>
            <select
              value={paletteKey}
              onChange={e => setPaletteKey(e.target.value)}
              className="text-[12px] rounded-full border border-black/15 px-3 py-1.5 bg-white"
            >
              {Object.entries(PALETTES).map(([k, p]) => (
                <option key={k} value={k}>{p.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 pb-3 flex flex-wrap gap-2">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`text-[12.5px] rounded-full border px-3.5 py-1.5 transition-colors ${
                tab === t.id ? "bg-black text-white border-black" : "bg-white text-black/70 border-black/15 hover:border-black/40"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {tab === "logos"    && <LogosScreen palette={palette} />}
        {tab === "applied"  && <AppliedScreen palette={palette} />}
        {tab === "palettes" && <PalettesScreen current={paletteKey} setCurrent={setPaletteKey} />}
        {tab === "tagline"  && <TaglineScreen />}
        {tab === "type"     && <TypeScreen palette={palette} />}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Logo SVGs — 6 distinct directions. Each renders against any palette.
   ───────────────────────────────────────────────────────────────────────── */
function LogoWordmark({ fg, accent, size = 64 }) {
  // Direction 1: bold geometric wordmark with custom Q tail.
  // We render "VER", a custom circular Q (with a diagonal arrow tail), then "O".
  return (
    <svg viewBox="0 0 360 90" height={size} style={{ display: "block" }}>
      <text x="0" y="68" fontFamily="'Inter', system-ui" fontSize="68" fontWeight="800" letterSpacing="-3" fill={fg}>VER</text>
      {/* Custom Q — concentric ring + diagonal tail */}
      <circle cx="195" cy="44" r="28" stroke={fg} strokeWidth="11" fill="none"/>
      <line x1="215" y1="64" x2="240" y2="84" stroke={accent} strokeWidth="11" strokeLinecap="round"/>
      <text x="252" y="68" fontFamily="'Inter', system-ui" fontSize="68" fontWeight="800" letterSpacing="-3" fill={fg}>O</text>
    </svg>
  );
}

function LogoQMark({ fg, accent, size = 96 }) {
  // Direction 2: standalone Q monogram — clean concentric ring with diagonal tail
  return (
    <svg viewBox="0 0 120 120" height={size} style={{ display: "block" }}>
      <rect width="120" height="120" rx="24" fill={fg}/>
      <circle cx="55" cy="60" r="28" stroke={accent} strokeWidth="9" fill="none"/>
      <line x1="73" y1="78" x2="92" y2="97" stroke={accent} strokeWidth="9" strokeLinecap="round"/>
    </svg>
  );
}

function LogoLowercase({ fg, accent, size = 64 }) {
  // Direction 3: friendlier lowercase wordmark "verqo." with accent dot
  return (
    <svg viewBox="0 0 280 80" height={size} style={{ display: "block" }}>
      <text x="0" y="58" fontFamily="'Inter', system-ui" fontSize="56" fontWeight="600" letterSpacing="-2" fill={fg}>verqo</text>
      <circle cx="218" cy="58" r="7" fill={accent}/>
    </svg>
  );
}

function LogoStacked({ fg, accent, size = 96 }) {
  // Direction 4: stacked V/Q monogram for app icon / favicon usage
  return (
    <svg viewBox="0 0 120 120" height={size} style={{ display: "block" }}>
      <rect width="120" height="120" rx="24" fill={accent}/>
      <text x="60" y="78" fontFamily="'Inter', system-ui" fontSize="68" fontWeight="900" textAnchor="middle" letterSpacing="-4" fill={fg}>VQ</text>
    </svg>
  );
}

function LogoOrbital({ fg, accent, size = 64 }) {
  // Direction 5: wordmark + orbital halo around the Q — implies "in-orbit / always-on"
  return (
    <svg viewBox="0 0 360 100" height={size} style={{ display: "block" }}>
      <ellipse cx="222" cy="50" rx="44" ry="14" stroke={accent} strokeWidth="3" fill="none" transform="rotate(-18 222 50)"/>
      <text x="0" y="68" fontFamily="'Inter', system-ui" fontSize="64" fontWeight="700" letterSpacing="-2" fill={fg}>VERQO</text>
    </svg>
  );
}

function LogoBracket({ fg, accent, size = 64 }) {
  // Direction 6: editorial wordmark wrapped in brackets — "verified data"
  return (
    <svg viewBox="0 0 360 100" height={size} style={{ display: "block" }}>
      <text x="0" y="65" fontFamily="'IBM Plex Mono', 'Menlo', monospace" fontSize="56" fontWeight="600" letterSpacing="-1" fill={accent}>[</text>
      <text x="30" y="65" fontFamily="'Inter', system-ui" fontSize="56" fontWeight="700" letterSpacing="-2" fill={fg}>VERQO</text>
      <text x="266" y="65" fontFamily="'IBM Plex Mono', 'Menlo', monospace" fontSize="56" fontWeight="600" letterSpacing="-1" fill={accent}>]</text>
    </svg>
  );
}

const LOGO_OPTIONS = [
  { id: "wordmark",  name: "1. Geometric wordmark + Q arrow",   desc: "Custom Q with an arrow tail — visually encodes 'forward motion' for wholesale. Most distinctive option on this list.", Comp: LogoWordmark },
  { id: "qmark",     name: "2. Standalone Q monogram",          desc: "Standalone app-icon-friendly mark. Pairs with any wordmark below for full lockups.", Comp: LogoQMark },
  { id: "lowercase", name: "3. Friendly lowercase + dot",       desc: "Calmer, more approachable. Better for buyer-facing pages and email signatures.", Comp: LogoLowercase },
  { id: "stacked",   name: "4. VQ monogram (app / favicon)",    desc: "Two-letter mark for tight spaces — favicon, app store icon, Twitter avatar.", Comp: LogoStacked },
  { id: "orbital",   name: "5. Wordmark + orbital halo",        desc: "Subtle ring above the Q — implies 'always-on infrastructure'. Good motion-design hook.", Comp: LogoOrbital },
  { id: "bracket",   name: "6. Editorial bracket wordmark",     desc: "[VERQO] — reads like a data field. Strong on dark backgrounds, B2B-credible.", Comp: LogoBracket },
];

function LogosScreen({ palette }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {LOGO_OPTIONS.map(({ id, name, desc, Comp }) => (
        <div key={id} className="rounded-2xl border border-black/10 bg-white overflow-hidden">
          {/* Logo on brand background */}
          <div className="px-8 py-10 flex items-center justify-center" style={{ background: palette.bg }}>
            <Comp fg={palette.fg} accent={palette.accent} size={70}/>
          </div>
          {/* Same logo on white */}
          <div className="px-8 py-8 flex items-center justify-center border-t border-black/5 bg-white">
            <Comp fg={palette.bg} accent={palette.bg} size={50}/>
          </div>
          <div className="px-5 py-3.5 bg-white border-t border-black/5">
            <div className="text-[13px] font-medium">{name}</div>
            <div className="text-[11.5px] text-black/55 mt-0.5 leading-snug">{desc}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Applied mockups — same logo in the contexts that matter most.
   ───────────────────────────────────────────────────────────────────────── */
function AppliedScreen({ palette }) {
  return (
    <div className="space-y-6">
      {/* Login header — replicates the existing /login page top-left brand */}
      <Mock title="Login header (top-left)" sub="Replaces TRAQO mark on /login. Direction 1 lockup.">
        <div className="rounded-xl border border-black/10 bg-white p-5">
          <div className="inline-flex items-center gap-2.5 rounded-md px-3 py-1.5" style={{ background: palette.bg }}>
            <LogoQMark fg={palette.fg} accent={palette.accent} size={28}/>
            <span className="font-bold tracking-tight text-[18px]" style={{ color: palette.fg, fontFamily: "'Inter', system-ui" }}>VERQO</span>
          </div>
        </div>
      </Mock>

      {/* Buyer portal header — replicates sidebar top */}
      <Mock title="Buyer / Seller portal sidebar" sub="Top-left brand in /buyer/* and /seller/* shells.">
        <div className="rounded-xl bg-white border border-black/10 p-5 max-w-md">
          <div className="inline-flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg grid place-items-center" style={{ background: palette.accent }}>
              <span className="font-extrabold text-[16px]" style={{ color: palette.accent_fg, fontFamily: "'Inter', system-ui" }}>V</span>
            </div>
            <div className="flex flex-col">
              <span className="font-bold tracking-tight text-[16px] leading-tight" style={{ fontFamily: "'Inter', system-ui" }}>VERQO</span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-black/45 -mt-0.5">Buyer Portal</span>
            </div>
          </div>
        </div>
      </Mock>

      {/* Favicon / app icon row */}
      <Mock title="Favicons & app icons" sub="Browser tab · mobile app store · social avatar — three sizes.">
        <div className="flex gap-4 items-center flex-wrap">
          {[16, 32, 64, 128].map(s => (
            <div key={s} className="text-center">
              <div className="inline-block rounded-md overflow-hidden ring-1 ring-black/10" style={{ background: palette.bg }}>
                <LogoQMark fg={palette.fg} accent={palette.accent} size={s}/>
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-black/45 mt-1.5">{s}×{s}</div>
            </div>
          ))}
        </div>
      </Mock>

      {/* Email signature */}
      <Mock title="Transactional email header" sub="Resend-rendered HTML email — what every notification email opens with.">
        <div className="rounded-xl border border-black/10 max-w-lg overflow-hidden">
          <div className="px-5 py-4 flex items-center gap-3" style={{ background: palette.bg }}>
            <LogoQMark fg={palette.fg} accent={palette.accent} size={32}/>
            <span className="font-bold text-[16px]" style={{ color: palette.fg, fontFamily: "'Inter', system-ui" }}>VERQO</span>
            <span className="ml-auto text-[10px] font-mono uppercase tracking-widest" style={{ color: palette.muted }}>The trust layer for B2B trade</span>
          </div>
          <div className="px-5 py-6 bg-white">
            <div className="text-[14px] font-semibold mb-1.5">New PO from Apex Wholesale</div>
            <div className="text-[12.5px] text-black/65">3 line items · $12,450 · confirm to issue invoice</div>
            <button className="mt-3 text-[12px] rounded-full px-4 py-2" style={{ background: palette.bg, color: palette.fg }}>Open in VERQO</button>
          </div>
        </div>
      </Mock>

      {/* Invoice footer */}
      <Mock title="Invoice PDF footer" sub="Generated invoices include a small brand strip + legal line.">
        <div className="rounded-xl border border-black/10 max-w-2xl overflow-hidden bg-white p-6 font-mono text-[11px]">
          <div className="border-b border-dashed border-black/15 pb-3 mb-3 text-black/60">INV-4195 · GlowCo Beauty → Apex Wholesale · $1,247.20 · due 6/7/2026</div>
          <div className="text-black/60 leading-relaxed">Issued via VERQO. Restocking fee: 10% applies if cancelled after packing or not picked up within 30 days. Disputes within QI window — verqo.com/claims</div>
          <div className="mt-3 flex items-center justify-between">
            <div className="inline-flex items-center gap-1.5">
              <div className="w-3.5 h-3.5 rounded-sm" style={{ background: palette.bg }}/>
              <span className="font-bold tracking-tight" style={{ color: palette.bg, fontFamily: "'Inter', system-ui" }}>VERQO</span>
            </div>
            <span className="text-[10px] text-black/45">verqo.com · Page 1 of 1</span>
          </div>
        </div>
      </Mock>

      {/* Storefront header */}
      <Mock title="Seller storefront header" sub="/s/glowco — where buyers land via the Launch Kit share link.">
        <div className="rounded-xl border border-black/10 max-w-2xl overflow-hidden">
          <div className="px-6 py-3 flex items-center justify-between border-b border-black/5" style={{ background: palette.bg }}>
            <div className="text-[11px] font-mono uppercase tracking-widest" style={{ color: palette.muted }}>Powered by</div>
            <div className="inline-flex items-center gap-2">
              <LogoQMark fg={palette.fg} accent={palette.accent} size={20}/>
              <span className="font-bold text-[13px]" style={{ color: palette.fg, fontFamily: "'Inter', system-ui" }}>VERQO</span>
            </div>
          </div>
          <div className="px-6 py-7 bg-white">
            <div className="display text-2xl font-bold mb-1">GlowCo Beauty</div>
            <div className="text-[13px] text-black/60">Clean beauty · Los Angeles · since 2019</div>
          </div>
        </div>
      </Mock>
    </div>
  );
}

function Mock({ title, sub, children }) {
  return (
    <div>
      <div className="mb-2 flex items-baseline gap-3 flex-wrap">
        <div className="text-[10.5px] font-mono uppercase tracking-widest text-black/50">{title}</div>
        <div className="text-[11.5px] text-black/45">{sub}</div>
      </div>
      {children}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Color palettes
   ───────────────────────────────────────────────────────────────────────── */
function PalettesScreen({ current, setCurrent }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {Object.entries(PALETTES).map(([key, p]) => (
        <button
          key={key}
          onClick={() => setCurrent(key)}
          className={`text-left rounded-2xl overflow-hidden border-2 transition-colors ${
            current === key ? "border-black" : "border-black/10 hover:border-black/30"
          }`}
        >
          <div className="px-5 py-7 flex items-end gap-3" style={{ background: p.bg }}>
            <LogoWordmark fg={p.fg} accent={p.accent} size={42}/>
            {current === key && (<span className="ml-auto text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: p.accent, color: p.accent_fg }}>Selected</span>)}
          </div>
          <div className="bg-white p-4">
            <div className="font-medium text-[13.5px]">{p.name}</div>
            <div className="text-[11.5px] text-black/55 leading-snug mt-0.5 mb-3">{p.sub}</div>
            <div className="flex gap-1.5">
              {[p.bg, p.surface, p.fg, p.accent].map((c) => (
                <div key={c} title={c} className="w-9 h-9 rounded-md border border-black/10 font-mono text-[9px] flex items-end justify-end p-0.5" style={{ background: c, color: c === p.bg || c === p.surface ? p.fg : p.bg }}>{c.toUpperCase()}</div>
              ))}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Tagline shortlist
   ───────────────────────────────────────────────────────────────────────── */
function TaglineScreen() {
  const [pick, setPick] = useState(null);
  return (
    <div className="space-y-2">
      <p className="text-[12.5px] text-black/55 mb-4">Click a tagline to lock it in visually — the rationale + hero usage example update below.</p>
      <div className="grid sm:grid-cols-2 gap-2">
        {TAGLINES.map((t, i) => (
          <button
            key={i}
            onClick={() => setPick(i)}
            className={`text-left rounded-xl border p-4 transition-colors ${
              pick === i ? "border-black bg-black/[0.04]" : "border-black/10 bg-white hover:border-black/30"
            }`}
          >
            <div className="text-[10px] font-mono uppercase tracking-widest text-black/40 mb-1">Option {i + 1}</div>
            <div className="text-[15px] font-medium tracking-tight" style={{ fontFamily: "'Inter', system-ui" }}>{t.line}</div>
            <div className="text-[11.5px] text-black/55 mt-1.5 leading-snug">{t.why}</div>
          </button>
        ))}
      </div>
      {pick !== null && (
        <div className="mt-6 rounded-2xl border border-black/15 bg-white p-6">
          <div className="text-[10.5px] font-mono uppercase tracking-widest text-black/40 mb-2">Hero preview</div>
          <div className="text-5xl font-bold tracking-tight" style={{ fontFamily: "'Inter', system-ui" }}>VERQO</div>
          <div className="text-2xl text-black/65 mt-2 tracking-tight">{TAGLINES[pick].line}</div>
          <div className="mt-3 text-[12px] text-black/50">Use as the landing-page H1 + subtitle, also as the email-template tagline, and as the OpenGraph description for `/s/:handle`.</div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Typography recommendation
   ───────────────────────────────────────────────────────────────────────── */
function TypeScreen({ palette }) {
  return (
    <div className="space-y-6">
      <div className="rounded-2xl bg-white border border-black/10 p-7" style={{ fontFamily: "'Inter', system-ui" }}>
        <div className="text-[11px] font-mono uppercase tracking-widest text-black/40 mb-3">Display + UI · Inter</div>
        <div className="text-5xl font-bold tracking-tight mb-1">Wholesale, verified.</div>
        <div className="text-xl text-black/60 mb-4">The trust layer for B2B trade.</div>
        <div className="text-[14px] text-black/75 max-w-xl leading-relaxed">
          Inter is Stripe's font (and Linear's, and Loom's, and Notion's). It reads as confident and modern without being trendy. Variable weight 400 → 800 covers everything from invoice body text to hero displays.
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-black/10 p-7" style={{ fontFamily: "'IBM Plex Mono', Menlo, monospace" }}>
        <div className="text-[11px] uppercase tracking-widest text-black/40 mb-3">Mono · IBM Plex Mono</div>
        <div className="text-[15px] mb-1">order_code = "ORD-PO-9412"</div>
        <div className="text-[15px] mb-1">deposit_pct = 30  // %</div>
        <div className="text-[15px] mb-1">restocking  = 10  // %</div>
        <div className="text-[13px] text-black/55 mt-3" style={{ fontFamily: "'Inter', system-ui" }}>
          Used for: invoice numbers, SKUs, transaction refs, debug chips. Plex Mono pairs natively with Inter (same metrics family).
        </div>
      </div>

      <div className="rounded-2xl overflow-hidden border border-black/10">
        <div className="p-6" style={{ background: palette.bg, color: palette.fg, fontFamily: "'Inter', system-ui" }}>
          <div className="text-[11px] font-mono uppercase tracking-widest mb-2" style={{ color: palette.muted }}>Hero · live preview on selected palette</div>
          <div className="text-5xl font-bold tracking-tight">Wholesale,</div>
          <div className="text-5xl font-bold tracking-tight" style={{ color: palette.accent }}>verified.</div>
          <div className="text-lg mt-3" style={{ color: palette.muted }}>VERQO is the trust layer for B2B trade — credit, prebooks, claims, and shipping in one portal.</div>
        </div>
      </div>
    </div>
  );
}
